import { ThemeProvider } from "@/providers/ThemeProvider";
import { QueryProvider } from "@/providers/QueryProvider";
import { ModeProvider } from "@/providers/ModeProvider";
import { ApiConfigProvider } from "@/providers/ApiConfigProvider";
import { ModeQuerySync } from "@/components/ModeQuerySync";
import { CompanyQuerySync } from "@/components/CompanyQuerySync";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { CompanyProvider } from "@/contexts/CompanyContext";
import App from "@/App";
import config from "@/appsettings.json";
import type { CompanyBasicInfoApi } from "@/types/api/user-context";

export type HostApp = 'app' | 'app-prod';

export interface AppContainerSettings {
  apiUrl?: string;
  financialsApiUrl?: string;
  host?: HostApp;
  /** External companies from userContext (used when host is 'app-prod') */
  externalCompanies?: CompanyBasicInfoApi[];
  /** Whether external companies are still loading */
  externalCompaniesLoading?: boolean;
}

interface AppContainerProps {
  settings?: AppContainerSettings;
}

export default function AppContainer({ settings }: AppContainerProps) {
  // Use settings.apiUrl if provided (from app-prod), otherwise fall back to local config
  const apiBaseUrl = settings?.apiUrl || config.apiBaseUrl;
  // Use settings.financialsApiUrl if provided (from app-prod), otherwise fall back to local config
  const financialsApiUrl = settings?.financialsApiUrl || (config as { financialsApiUrl?: string }).financialsApiUrl;

  return (
    <ModeProvider defaultMode="demo">
      <QueryProvider>
        <ModeQuerySync>
          <CompanyProvider 
            externalCompanies={settings?.externalCompanies}
            externalCompaniesLoading={settings?.externalCompaniesLoading}
          >
            <ApiConfigProvider apiBaseUrl={apiBaseUrl} financialsApiUrl={financialsApiUrl}>
              <CompanyQuerySync>
                <TooltipProvider>
                  <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
                    <Toaster richColors duration={5000} closeButton />
                    <App />
                  </ThemeProvider>
                </TooltipProvider>
              </CompanyQuerySync>
            </ApiConfigProvider>
          </CompanyProvider>
        </ModeQuerySync>
      </QueryProvider>
    </ModeProvider>
  );
}
